export const PREVIEW_ATTR = "data-publisher-asset-preview";

type Original = {
  from: string;
  to: string;
  src: string | null;
  srcset: string | null;
  sizes: string | null;
  sources: [HTMLSourceElement, string | null][];
};

function setOrRemove(el: Element, name: string, value: string | null) {
  if (value === null) el.removeAttribute(name);
  else el.setAttribute(name, value);
}

// A src React changed since the swap is React's, so it's never restored over.
export function createPreviewSwaps(
  srcOf: (img: HTMLImageElement) => string | null,
) {
  const originals = new Map<HTMLImageElement, Original>();
  let swaps = new Map<string, string>();

  const restore = (img: HTMLImageElement, original: Original) => {
    setOrRemove(img, "src", original.src);
    setOrRemove(img, "srcset", original.srcset);
    setOrRemove(img, "sizes", original.sizes);
    for (const [source, srcset] of original.sources) {
      setOrRemove(source, "srcset", srcset);
    }
  };

  const apply = () => {
    for (const [img, original] of originals) {
      const ours = img.isConnected && img.getAttribute("src") === original.to;
      if (ours && swaps.get(original.from) === original.to) continue;
      if (ours) restore(img, original);
      img.removeAttribute(PREVIEW_ATTR);
      originals.delete(img);
    }
    if (!swaps.size) return;
    for (const img of document.querySelectorAll("img")) {
      if (originals.has(img)) continue;
      const from = srcOf(img);
      const to = from === null ? undefined : swaps.get(from);
      if (from === null || to === undefined) continue;
      const picture =
        img.parentElement instanceof HTMLPictureElement
          ? img.parentElement
          : null;
      const sources = picture
        ? [...picture.querySelectorAll("source")].map(
            (s) =>
              [s, s.getAttribute("srcset")] as [
                HTMLSourceElement,
                string | null,
              ],
          )
        : [];
      originals.set(img, {
        from,
        to,
        src: img.getAttribute("src"),
        srcset: img.getAttribute("srcset"),
        sizes: img.getAttribute("sizes"),
        sources,
      });
      for (const [source] of sources) source.removeAttribute("srcset");
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.setAttribute("src", to);
      img.setAttribute(PREVIEW_ATTR, "");
    }
  };

  return {
    set(next: { from: string; to: string }[]) {
      swaps = new Map(next.map((s) => [s.from, s.to]));
      apply();
    },
    apply,
  };
}
